import { memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { DateFilter } from '~/components/Filters/DateFilter/DateFilter';
import { type BaseFilterCardProps, FilterCard } from '~/components/Filters/FilterCard';
import {
  removeFilterAction,
  setTakenDateFilterEndDateAction,
  setTakenDateFilterStartDateAction,
  setTakenDateFilterTypeAction,
} from '~/store/filters/actions';
import {
  takenDateFilterDateTypeSelector,
  takenDateFilterEndDateSelector,
  takenDateFilterStartDateSelector,
} from '~/store/filters/selectors';
import { type DateRangeType, type SelectedFilter } from '~/store/filters/types';

export type TakenDateFilterCardProps = BaseFilterCardProps;

export const TakenDateFilterCard = memo((props: TakenDateFilterCardProps) => {
  const dispatch = useDispatch();
  const dateType = useSelector(takenDateFilterDateTypeSelector);
  const startDate = useSelector(takenDateFilterStartDateSelector);
  const endDate = useSelector(takenDateFilterEndDateSelector);

  const onDateTypeChange = useCallback(
    (type: DateRangeType) => {
      dispatch(setTakenDateFilterTypeAction({ type }));
    },
    [dispatch],
  );

  const onStartDateChange = useCallback(
    (date: Date | [Date, Date] | null) => {
      if (Array.isArray(date)) return;
      dispatch(setTakenDateFilterStartDateAction({ startDate: date }));
    },
    [dispatch],
  );

  const onEndDateChange = useCallback(
    (date: Date | [Date, Date] | null) => {
      if (Array.isArray(date)) return;
      dispatch(setTakenDateFilterEndDateAction({ endDate: date }));
    },
    [dispatch],
  );

  const onRemoveFilter = (filter: SelectedFilter) => {
    dispatch(removeFilterAction({ filter }));
  };

  return (
    <FilterCard currentFilter="dateTaken" onRemoveFilter={onRemoveFilter} {...props}>
      <DateFilter
        startDate={startDate}
        endDate={endDate}
        selectedType={dateType}
        onStartDateChange={onStartDateChange}
        onEndDateChange={onEndDateChange}
        onTypeChange={onDateTypeChange}
      />
    </FilterCard>
  );
});

TakenDateFilterCard.displayName = 'TakenDateFilterCard';
